/**
 * 🌏 **대화창에 쓰는 말** — 문답을 그 나라 말로 만들어 놓고 창은 한국어면 반쪽이다.
 *
 * ## 왜 여기만 다국어인가
 * 이 서비스는 한국어 전용이다(`<html lang="ko">`). 화면 전체를 옮기는 건 별개의 큰 일이고,
 * 1차 타깃(0~7세 자녀를 둔 국내 보호자)에게는 지금이 맞다.
 *
 * 그런데 **대화창만은 다르다.** 여기가 2·3차 타깃(어린이집·도서관·경주 관광객)이 실제로
 * 말을 거는 자리이고, 문답은 이미 그 말로 나간다. 답은 영어인데 「보내기」·「닫기」가
 * 한국어면, 옮겨 둔 보람이 절반만 남는다.
 *
 * ## 이 표에 없는 것
 * NPC 이름(`title_ko`)은 **운영자가 정한다** — 여기서 옮기지 않는다.
 * 아이 이름·마을 이름을 코드가 번역하기 시작하면 운영자가 손댈 수 없는 글이 생긴다.
 */
import type { Lang } from "./ai";
import { NPC_CHAT_LOG_DAYS } from "./npcChatLog";

export interface NpcChatText {
  /** 창 제목에 쓸 기본 이름(운영자가 이름을 안 붙였을 때만). */
  fallbackName: string;
  close: string;
  /** 자유 대화 입력칸. */
  askLabel: string;
  askPlaceholder: string;
  send: string;
  /** 🗒️ 자유 대화 입력 칸 아래 — 질문을 며칠 보관하는지(FR-NPC-05 · 방침 6항과 같은 말). 숫자는 NPC_CHAT_LOG_DAYS */
  keepNote: string;
  /** 물어볼 것이 하나도 없을 때. */
  empty: string;
  /** 어른 확인. */
  gateTitle: string;
  gateWhy: string;
  /**
   * 셈 문제를 **꺼내는 단추**. 누르기 전엔 셈이 화면에 없다 —
   * 「아래 셈을 풀어 주세요」라고 적었더니 아래에 아무것도 없었다(2026-09-17 운영에서 걸어 봄).
   * 단추는 누르면 일어나는 일을 말한다.
   */
  gateHint: string;
  gateWrong: string;
  gateOpen: string;
  /** 🌏 자유 대화가 안 될 때 말풍선에 띄우는 말 — 갈래는 `DocentFail`(엣지가 주는 error 코드를 묶은 것) */
  fail: Record<DocentFail, string>;
}

/**
 * 💬 **자유 대화가 안 된 까닭** — 엣지 `/api/npc/ask` 의 `error` 를 화면이 말할 갈래로 묶는다 (2026-10-18).
 *
 * 창은 네 말로 옮겨 놨는데 **실패 문장만 한국어**였다 — 엣지가 주는 `reason` 과 웹의 「인터넷이 끊겼어요」를
 * 그대로 띄워서. 영어로 묻던 관광객이 상한에 걸리면 한국어 한 줄만 받았다.
 */
export type DocentFail = "offline" | "busy" | "closed" | "resting" | "unknown";

/** 엣지 `error` 코드(apps/edge/src/aiDocent.ts) → 갈래. 모르는 코드는 unknown. */
export function docentFailOf(error: string | undefined): DocentFail {
  switch (error) {
    case "too_many":
    case "daily_limit":
      return "busy";
    case "not_open":
      return "closed";
    case "ai_unavailable":
    case "provider_unavailable": // 고른 모델의 열쇠가 없다
    case "no_kb": // 아는 것(지식 묶음)이 아직 없다
    case "unavailable": // 엣지 저장소가 없다
    case "migration_required": // 표가 아직 안 만들어졌다
      return "resting";
    default:
      return "unknown";
  }
}

/**
 * 말풍선에 띄울 문장. 한국어는 **엣지가 준 이유가 있으면 그대로**(「아이가 보는 자리라」처럼 더 자세하다),
 * 다른 말은 이 표에서 — 엣지 문장을 옮겨 적지 않는다.
 */
export function docentFailSay(lang: Lang | undefined, kind: DocentFail, reason?: string): string {
  if ((lang ?? "ko") === "ko" && reason?.trim()) return reason.trim();
  return npcChatText(lang).fail[kind];
}

export const NPC_CHAT_TEXT: Readonly<Record<Lang, NpcChatText>> = {
  ko: {
    fallbackName: "안내 요정",
    close: "닫기",
    askLabel: "자유롭게 물어보기",
    askPlaceholder: "궁금한 것을 물어보세요…",
    send: "보내기",
    keepNote: `물어본 말은 더 좋은 대답을 만들려고 ${NPC_CHAT_LOG_DAYS}일 동안 보관한 뒤 지워요. 이름·전화번호는 적지 말아 주세요.`,
    empty: "아직 물어볼 수 있는 이야기가 없어요.",
    gateTitle: "어른이 함께 보고 있나요?",
    gateWhy: "자유롭게 묻고 답하는 칸이라, 보호자·선생님이 함께 볼 때만 열어요.",
    gateHint: "🔢 셈 문제 보기",
    gateWrong: "다시 한 번 확인해 주세요.",
    gateOpen: "열기",
    fail: {
      offline: "인터넷이 잠깐 끊겼어요. 아래 버튼으로 물어봐 주세요!",
      busy: "조금 쉬었다가 다시 물어봐 주세요. 그동안은 아래 버튼으로 물어봐요!",
      closed: "이 친구는 지금 자유 대화를 안 해요. 아래 버튼으로 물어봐 주세요!",
      resting: "지금은 자유 대화를 쉬고 있어요. 아래 버튼으로 물어봐 주세요!",
      unknown: "지금은 대답하기 어려워요. 아래 버튼으로 물어봐 주세요!",
    },
  },
  en: {
    fallbackName: "Guide",
    close: "Close",
    askLabel: "Ask a question",
    askPlaceholder: "Ask anything you're curious about…",
    send: "Send",
    keepNote: `Questions are kept for ${NPC_CHAT_LOG_DAYS} days to improve answers, then deleted. Please don't type names or phone numbers.`,
    empty: "There's nothing to ask about just yet.",
    gateTitle: "Is a grown-up here with you?",
    gateWhy: "This is a free chat, so we only open it when a parent or teacher is watching.",
    gateHint: "🔢 Show a math question",
    gateWrong: "Please check again.",
    gateOpen: "Open",
    fail: {
      offline: "The internet dropped for a moment. Please use the buttons below!",
      busy: "Let's take a short break and ask again soon. Meanwhile, try the buttons below!",
      closed: "This friend isn't chatting freely right now. Please use the buttons below!",
      resting: "Free chat is resting right now. Please use the buttons below!",
      unknown: "I can't answer right now. Please use the buttons below!",
    },
  },
  ja: {
    fallbackName: "あんないの妖精",
    close: "閉じる",
    askLabel: "自由に質問する",
    askPlaceholder: "気になることを聞いてみてください…",
    send: "送る",
    keepNote: `質問はよりよい答えを作るため${NPC_CHAT_LOG_DAYS}日間保管したあと削除します。名前や電話番号は書かないでください。`,
    empty: "まだ聞けるお話がありません。",
    gateTitle: "大人の方がそばにいますか？",
    gateWhy: "自由に質問できる欄なので、保護者・先生がご覧のときだけ開きます。",
    gateHint: "🔢 計算問題を出す",
    gateWrong: "もう一度ご確認ください。",
    gateOpen: "ひらく",
    fail: {
      offline: "インターネットが少し切れました。下のボタンで聞いてみてね！",
      busy: "少し休んでから、また聞いてね。そのあいだは下のボタンで聞いてみてね！",
      closed: "このお友だちはいま自由なおしゃべりをしていません。下のボタンで聞いてみてね！",
      resting: "いまは自由なおしゃべりをお休みしています。下のボタンで聞いてみてね！",
      unknown: "いまは答えられません。下のボタンで聞いてみてね！",
    },
  },
  zh: {
    fallbackName: "向导精灵",
    close: "关闭",
    askLabel: "自由提问",
    askPlaceholder: "有什么想问的都可以…",
    send: "发送",
    keepNote: `提问会保存${NPC_CHAT_LOG_DAYS}天用于改进回答，之后删除。请不要输入姓名或电话号码。`,
    empty: "还没有可以询问的内容。",
    gateTitle: "身边有大人陪着吗？",
    gateWhy: "这是自由问答区，只在家长或老师陪同时开启。",
    gateHint: "🔢 显示算式题",
    gateWrong: "请再确认一次。",
    gateOpen: "打开",
    fail: {
      offline: "网络暂时断开了。请用下面的按钮提问吧！",
      busy: "先休息一下再问吧。这段时间请用下面的按钮提问！",
      closed: "这位朋友现在不进行自由聊天。请用下面的按钮提问吧！",
      resting: "自由聊天现在休息中。请用下面的按钮提问吧！",
      unknown: "现在没法回答。请用下面的按钮提问吧！",
    },
  },
};

/** 그 말의 글. 모르는 말은 한국어로(원문). */
export function npcChatText(lang: Lang | undefined): NpcChatText {
  return NPC_CHAT_TEXT[lang ?? "ko"] ?? NPC_CHAT_TEXT.ko;
}
